/** Shared back-camera constraints for collection scan, join QR, and any future camera UI. */
export const SCANNER_CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  audio: false,
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1280 },
    height: { ideal: 720 },
  },
};

/**
 * Cross-route hand-off: the getUserMedia call starts during the tap gesture
 * (required on iOS Safari) but navigation must NOT wait for it — camera
 * spin-up + the permission prompt can take seconds and would make the tap
 * feel dead. We stash the in-flight promise and the scan page awaits it.
 */
let pending: Promise<MediaStream | null> | null = null;

function stopTracks(stream: MediaStream | null): void {
  if (!stream) return;
  for (const t of stream.getTracks()) t.stop();
}

/**
 * Request the shared scanner camera. Call from a user gesture on iOS Safari.
 * Once granted for this origin, later calls reuse permission without re-prompting.
 */
export async function requestCameraStream(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Camera API not available in this browser');
  }
  return navigator.mediaDevices.getUserMedia(SCANNER_CAMERA_CONSTRAINTS);
}

/**
 * Same as {@link requestCameraStream}, but swallows errors for navigation hand-offs
 * where the destination page can fall back to an inline permission prompt.
 */
export async function requestScannerCameraForNavigation(): Promise<MediaStream | null> {
  try {
    return await requestCameraStream();
  } catch {
    return null;
  }
}

/**
 * Hold at most one in-flight camera acquisition until the consumer route
 * mounts. Call synchronously inside the tap handler (before navigating) so
 * the getUserMedia call keeps its user-gesture provenance.
 */
export function stashScannerEntryStream(streamPromise: Promise<MediaStream | null>): void {
  discardScannerEntryStream();
  pending = streamPromise;
}

/** True when a tap-initiated camera acquisition is waiting for the scan page. */
export function hasScannerEntryStream(): boolean {
  return pending !== null;
}

/** Consumer takes ownership — attach with `startWithStream` or stop tracks on failure. */
export function takeScannerEntryStream(): Promise<MediaStream | null> | null {
  const next = pending;
  pending = null;
  return next;
}

/** Drop stashed pixels when the consumer rejects before attaching (never ran take). */
export function discardScannerEntryStream(): void {
  if (!pending) return;
  const stale = pending;
  pending = null;
  void stale.then(stopTracks, () => {});
}
